import * as React from 'react';
import * as styles from './QuoteCarousel.module.scss';
import { Container } from '../container/Container';
import { CarouselHeader } from '../carouselHeader/CarouselHeader';
import { useVisitCount } from '@/hooks/useVisitCount';

import quoteVideo1 from '@/images/quote/quote-video-1.mp4';
import quoteVideo2 from '@/images/quote/quote-video-2.mp4';
import quoteVideo3 from '@/images/quote/quote-video-3.mp4';
import quoteVideo4 from '@/images/quote/quote-video-4.mp4';
import quoteVideo5 from '@/images/quote/quote-video-5.mp4';
import quoteVideo1image from '@/images/quote/quote-video-1-static.png';
import quoteVideo2image from '@/images/quote/quote-video-2-static.png';
import quoteVideo3image from '@/images/quote/quote-video-3-static.png';
import quoteVideo4image from '@/images/quote/quote-video-4-static.png';
import quoteVideo5image from '@/images/quote/quote-video-5-static.png';
import { PauseIcon } from '../icons/PauseIcon';
import { PlayIcon } from '../icons/PlayIcon';
import { useCarouselToggle } from '@/hooks/useCarouselToggle';
import clsx from 'clsx';

const images = [quoteVideo1image, quoteVideo2image, quoteVideo3image, quoteVideo4image, quoteVideo5image];
const videos = [quoteVideo1, quoteVideo2, quoteVideo3, quoteVideo4, quoteVideo5];

type Quote = { testimonial?: string | null; clientDescription?: string | null };

export const QuoteCarousel = ({
  title,
  quotes,
  size = 'md',
}: {
  title?: string;
  quotes: Quote[];
  size?: 'md' | 'lg';
}) => {
  const { activeItemIndex, handlePrevClick, handleNextClick } = useCarouselToggle(quotes.length);
  const visitCount = useVisitCount();
  const assetIndex = visitCount % videos.length;
  const activeQuote = quotes[activeItemIndex];
  const [isPaused, setIsPaused] = React.useState(false);
  const videoRef = React.useRef<HTMLVideoElement>(null);

  const handlePausePlayClick = () => {
    setIsPaused((prev) => {
      const newPausedState = !prev;
      if (videoRef.current) {
        if (newPausedState) {
          videoRef.current.pause();
        } else {
          videoRef.current.play();
        }
      }
      return newPausedState;
    });
  };

  return (
    <div className={styles.quoteCarousel} role="region" aria-label={title || 'Testimonials carousel'}>
      <button
        className={styles.quoteCarousel__navIconBtn}
        onClick={handlePausePlayClick}
        aria-label={isPaused ? 'Play background video' : 'Pause background video'}
        aria-pressed={isPaused}
      >
        {isPaused ? <PlayIcon aria-hidden="true" /> : <PauseIcon aria-hidden="true" />}
      </button>
      <img
        src={images[assetIndex]}
        className={styles.quoteCarousel__image}
        alt="" // Decorative image, serves as fallback
        aria-hidden="true"
      />
      <video
        ref={videoRef}
        autoPlay
        muted
        loop
        playsInline
        className={styles.quoteCarousel__video}
        aria-hidden="true" // Video is decorative, the quotes are the main content
      >
        <source src={videos[assetIndex]} type="video/mp4" />
      </video>
      <Container>
        <div className={styles.quoteCarousel__inner}>
          {quotes.length > 1 ? (
            <CarouselHeader className={styles.quoteCarousel__header}>
              <CarouselHeader.Title>{title}</CarouselHeader.Title>
              <CarouselHeader.Navigation onPrevClick={handlePrevClick} onNextClick={handleNextClick} />
            </CarouselHeader>
          ) : null}
          <div
            className={styles.quoteCarousel__body}
            role="group"
            aria-roledescription="slide"
            aria-label={`Slide ${activeItemIndex + 1} of ${quotes.length}`}
          >
            <blockquote>
              <p className={clsx(styles.quoteCarousel__quote, size === 'lg' && styles.quoteCarousel__quote___bg)}>
                "{activeQuote.testimonial}"
              </p>
              <footer className={styles.quoteCarousel__author}>{activeQuote.clientDescription}</footer>
            </blockquote>
          </div>
        </div>
      </Container>
    </div>
  );
};
